import { useState, useMemo, useRef, useEffect } from 'react';
import Rolodex from './Rolodex';
import { CategoryFilter } from './CategoryFilter';
import { navigate } from './App';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { renderToString } from 'react-dom/server';
import { Briefcase, Calendar, MapPin } from 'lucide-react';
import { UnifiedMapControls } from './components/MapControls';
import { motion } from 'framer-motion';
import Portal from './Portal';
// Group details now open as a full page via navigation

// Utility: detect mobile
const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 640;

// Helper: Map auto-fit component
function MapAutoFit({ locations, zoneCenter }) {
    const map = useMap();
    const prevLocations = useRef();

    useEffect(() => {
        if (!map) return;
        if (!locations || locations.length === 0) {
            // No businesses: center on zone
            if (zoneCenter && Array.isArray(zoneCenter) && zoneCenter.length === 2) {
                map.flyTo(zoneCenter, 12, { duration: 1.2, easeLinearity: 0.25 });
            } else {
                map.flyTo([32.08, 34.78], 12, { duration: 1.2, easeLinearity: 0.25 });
            }
            return;
        }
        // Only update if locations changed
        if (JSON.stringify(prevLocations.current) === JSON.stringify(locations)) return;
        prevLocations.current = locations;

        if (locations.length === 1) {
            // One business: zoom in closer
            map.flyTo([locations[0].lat, locations[0].lng], isMobile() ? 14 : 15, { duration: 1.2, easeLinearity: 0.25 });
        } else {
            // Multiple: fit bounds with padding
            const bounds = locations.map(loc => [loc.lat, loc.lng]);
            map.flyToBounds(bounds, {
                padding: isMobile() ? [40, 40] : [80, 80],
                duration: 1.2,
                easeLinearity: 0.25
            });
        }
    }, [locations, zoneCenter, map]);
    return null;
}
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Leaflet default icon fix for bundlers
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

// Business marker icon with Briefcase icon (matching AroundMeMap)
const businessIcon = new L.DivIcon({
    html: `
        <div style="
            width: 36px;
            height: 36px;
            background: #3b82f6;
            border: 3px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        ">
            ${renderToString(<Briefcase size={20} color="white" strokeWidth={2.5} />)}
        </div>
    `,
    className: '',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18]
});

// Event marker icon with Calendar icon (matching AroundMeMap)
const eventIcon = new L.DivIcon({
    html: `
        <div style="
            width: 36px;
            height: 36px;
            background: #ef4444;
            border: 3px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        ">
            ${renderToString(<Calendar size={20} color="white" strokeWidth={2.5} />)}
        </div>
    `,
    className: '',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18]
});

// Zone marker icon with MapPin icon
const zoneIcon = new L.DivIcon({
    html: `
        <div style="
            width: 36px;
            height: 36px;
            background: #E5B55F;
            border: 3px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        ">
            ${renderToString(<MapPin size={20} color="white" strokeWidth={2.5} />)}
        </div>
    `,
    className: '',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18]
});

/**
 * Unified Filtered Rolodex View Component
 *
 * Provides filtering, view modes (rolodex/compact/map), and category filtering
 * for both businesses/events and groups.
 */
export const FilteredRolodexView = ({
    items,
    chatId,
    categories = [],
    type = 'business',
    fromView,
    zone,
    onDelete,
    showMap = type !== 'group',
    viewMode = 'rolodex',
    onViewModeChange = null,
    showFilters = false,
    onToggleFilters = null,
    onFilterChange = null,
    hideControls = false,
    zoneCenter = null, // [lat, lng] (optional)
    favoritesLists = [],
    onToggleFavorite = null,
    onCreateFavoriteList = null
}) => {
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [pendingCategories, setPendingCategories] = useState([]);
    const [showOnlyUpcoming, setShowOnlyUpcoming] = useState(false);
    const [currentDisplayMode, setCurrentDisplayMode] = useState(viewMode);
    const [internalShowFilters, setInternalShowFilters] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);

    // Use external state if provided, otherwise use internal
    const activeViewMode = onViewModeChange ? viewMode : currentDisplayMode;
    const activeShowFilters = onToggleFilters ? showFilters : internalShowFilters;

    const applyFilters = () => {
        setSelectedCategories(pendingCategories);
        if (onFilterChange) {
            onFilterChange(pendingCategories.length);
        }
        if (onToggleFilters) {
            onToggleFilters();
        } else {
            setInternalShowFilters(false);
        }
    };

    const toggleCategory = (cat) => {
        setPendingCategories((prev) =>
            prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
        );
    };

    const clearAllFilters = () => {
        setPendingCategories([]);
        setSelectedCategories([]);
        if (onFilterChange) {
            onFilterChange(0);
        }
        if (onToggleFilters) {
            onToggleFilters();
        } else {
            setInternalShowFilters(false);
        }
    };

    const filteredItems = useMemo(() => {
        if (!items) return [];

        return items.filter((item) => {
            // Category filtering - handle both business_categories and categories
            const itemCategories = type === 'group'
                ? (item.categories || [])
                : (item.business_categories || []);

            const matchCategory =
                selectedCategories.length === 0 ||
                selectedCategories.some((cat) => itemCategories.includes(cat));

            // Upcoming events filter
            const isUpcoming =
                type !== 'event' || !showOnlyUpcoming
                    ? true
                    : (() => {
                        const now = new Date();
                        const nextWeek = new Date();
                        nextWeek.setDate(now.getDate() + 7);
                        const eventDate = new Date(item.event_time);
                        return eventDate >= now && eventDate <= nextWeek;
                    })();

            return matchCategory && isUpcoming;
        });
    }, [items, selectedCategories, showOnlyUpcoming, type]);

    if (items == null) {
        // Loading: ghost cards in a grid that match the real layout
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="bg-surface rounded-2xl border border-line-subtle shadow-card overflow-hidden">
                        <div className="aspect-[16/10] bg-gradient-to-r from-surface-sunken via-surface-muted to-surface-sunken animate-pulse-soft" />
                        <div className="p-5 space-y-2.5">
                            <div className="h-3 w-1/3 rounded-pill bg-surface-sunken animate-pulse-soft" />
                            <div className="h-4 w-3/4 rounded-md bg-surface-sunken animate-pulse-soft" />
                            <div className="h-3 w-full rounded-md bg-surface-sunken animate-pulse-soft" />
                            <div className="h-3 w-5/6 rounded-md bg-surface-sunken animate-pulse-soft" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center text-center py-16 px-6 surface-soft rounded-2xl">
                <div className="relative mb-5">
                    <span className="absolute inset-0 bg-brand-200/40 rounded-pill blur-2xl" aria-hidden="true" />
                    <span className="relative inline-flex items-center justify-center w-20 h-20 rounded-pill bg-surface ring-4 ring-brand-50">
                        <Briefcase className="w-9 h-9 text-brand-500" strokeWidth={1.5} />
                    </span>
                </div>
                <h3 className="heading-sm text-ink mb-2">אין כאן עדיין כלום</h3>
                <p className="text-sm text-ink-muted max-w-xs">
                    כשיתווסף תוכן חדש לקהילה, הוא יופיע כאן.
                </p>
            </div>
        );
    }

    const validLocations = filteredItems
        .map((biz) => {
            try {
                const loc = JSON.parse(biz.location);
                if (loc && loc.lat && loc.lng) {
                    return { ...biz, lat: loc.lat, lng: loc.lng };
                }
            } catch { }
            return null;
        })
        .filter(Boolean);

    // Use zoneCenter if provided and no businesses
    const defaultCenter = validLocations.length > 0
        ? [validLocations[0].lat, validLocations[0].lng]
        : (zoneCenter && Array.isArray(zoneCenter) && zoneCenter.length === 2 ? zoneCenter : [32.08, 34.78]);

    const navigateToBusiness = (id) => {
        navigate(`?entity=${id}`);
    };

    const handleCloseGroupModal = () => {
        setSelectedGroup(null);
    };

    const navigateToGroup = (group) => {
        const id = group.group_id || group.id;
        if (!id) return;
        // Use zone prop if available, otherwise extract from group data
        const zoneId = zone || group.zone_id || group.zone;
        navigate('group', `group_id=${id}${zoneId ? `&zone=${encodeURIComponent(zoneId)}` : ''}`);
    };

    return (
        <div className="space-y-4 relative z-10 px-2 sm:px-4">
            {/* Group View now navigates to full page; overlay removed */}

            {/* Floating Filter Panel - Above map controls - Using Portal to escape stacking context */}
            {activeShowFilters && (
                <Portal>
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center">
                        <div className="bg-white rounded-2xl p-6 space-y-4 w-80 shadow-elevated border border-line-subtle text-right z-[10000]">
                            <h2 className="heading-md mb-3 text-center">סינון לפי קטגוריה</h2>

                            <CategoryFilter
                                categories={categories}
                                selected={pendingCategories}
                                toggleCategory={toggleCategory}
                                onClearAll={clearAllFilters}
                            />

                            <div className="flex justify-between mt-6 gap-2">
                                <button
                                    onClick={applyFilters}
                                    className="flex-1 px-4 py-2.5 bg-brand-400 hover:bg-brand-500 text-white rounded-xl font-semibold transition-all duration-250"
                                >
                                    החל סינון
                                </button>
                                <button
                                    onClick={() => onToggleFilters ? onToggleFilters() : setInternalShowFilters(false)}
                                    className="flex-1 px-4 py-2.5 bg-surface-muted hover:bg-surface-sunken text-ink rounded-xl font-semibold transition-all duration-250"
                                >
                                    ביטול
                                </button>
                            </div>
                        </div>
                    </div>
                </Portal>
            )}


            {/* Upcoming toggle */}
            {type === 'event' && (
                <div className="flex justify-center">
                    <label className="flex flex-row-reverse items-center gap-2 bg-white px-4 py-2 rounded-full border border-line shadow-sm cursor-pointer hover:bg-surface-muted transition-all duration-250">
                        <input
                            type="checkbox"
                            checked={showOnlyUpcoming}
                            onChange={() => setShowOnlyUpcoming((prev) => !prev)}
                            className="accent-brand-400 w-4 h-4"
                        />
                        <span className="text-sm font-medium text-ink">מפגשים בשבוע הקרוב בלבד</span>
                    </label>
                </div>
            )}

            {activeViewMode === 'compact' ? (
                <div className="w-full">
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2 sm:gap-2.5 w-full max-w-6xl mx-auto">
                        {filteredItems.map((item, idx) => {
                            const isZone = item.type === 'zone';

                            if (isZone) {
                                return (
                                    <motion.button
                                        key={item.zone_id}
                                        type="button"
                                        onClick={() => navigate('zone-details', `zone=${encodeURIComponent(item.zone_id)}`)}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.2, delay: Math.min(idx * 0.02, 0.24) }}
                                        whileHover={{ y: -1 }}
                                        whileTap={{ scale: 0.97 }}
                                        className="group flex flex-col items-center gap-1.5 p-2 bg-surface rounded-lg ring-1 ring-line-subtle hover:ring-brand-200 hover:shadow-card transition-[box-shadow,ring-color] duration-200 ease-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                    >
                                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-ink-inverse shadow-sm ring-2 ring-white">
                                            <MapPin size={18} strokeWidth={2} />
                                        </span>
                                        <h3 className="font-semibold text-2xs text-ink text-center leading-tight line-clamp-1 w-full">
                                            {item.business_name || item.zone_id}
                                        </h3>
                                    </motion.button>
                                );
                            }

                            const coverMedia = type === 'group'
                                ? (item.image || item.image_key || item.cover_image)
                                : (item.cover_image || item.image);
                            const title = item.name || item.business_name || item.group_name;
                            const itemId = item.business_id || item.id;
                            const key = itemId || `group-${title}-${idx}`;
                            const founderImg = item.founder_image_url;

                            return (
                                <motion.button
                                    key={key}
                                    type="button"
                                    onClick={() => type === 'group' ? navigateToGroup(item) : navigateToBusiness(itemId)}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.2, delay: Math.min(idx * 0.02, 0.24) }}
                                    whileHover={{ y: -1 }}
                                    whileTap={{ scale: 0.97 }}
                                    className="group flex flex-col w-full bg-surface rounded-lg ring-1 ring-line-subtle hover:ring-brand-200 hover:shadow-card transition-[box-shadow,ring-color] duration-200 ease-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                >
                                    <div className="relative w-full">
                                        <div className="aspect-square w-full overflow-hidden bg-surface-muted rounded-t-lg">
                                            {coverMedia ? (
                                                <img
                                                    src={coverMedia}
                                                    alt={title}
                                                    loading="lazy"
                                                    className="w-full h-full object-cover transition-transform duration-500 ease-smooth group-hover:scale-[1.05]"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-brand-50 to-secondary-50 text-brand-400">
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7l9-4 9 4M3 7v10l9 4 9-4V7M3 7l9 4 9-4M12 11v10" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                        {founderImg && (
                                            <img
                                                src={founderImg}
                                                alt=""
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                className="absolute -bottom-2 end-1 w-6 h-6 rounded-pill object-cover ring-2 ring-white shadow-sm"
                                            />
                                        )}
                                    </div>
                                    <div className="px-1.5 pt-2.5 pb-1.5">
                                        <h3 className="font-semibold text-2xs text-ink line-clamp-1 leading-tight text-right">{title}</h3>
                                    </div>
                                </motion.button>
                            );
                        })}
                    </div>
                </div>
            ) : activeViewMode === 'map' && showMap ? (
                <div className="h-[400px] w-full rounded-xl overflow-hidden z-0">
                    <MapContainer center={defaultCenter} zoom={12} scrollWheelZoom={true} className="h-full w-full z-0" animate={true}>
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attributionControl={false}
                        />
                        <MapAutoFit locations={validLocations} zoneCenter={zoneCenter} />
                        {validLocations.map((biz) => {
                            // Determine marker type based on type property
                            const isEvent = biz.event_time || biz.type === 'event';
                            const isZone = biz.type === 'zone';
                            const markerIcon = isZone ? zoneIcon : (isEvent ? eventIcon : businessIcon);

                            return (
                                <Marker
                                    key={biz.business_id || biz.id || biz.zone_id}
                                    position={[biz.lat, biz.lng]}
                                    icon={markerIcon}
                                >
                                    <Popup className="custom-popup" maxWidth={160}>
                                        {isZone ? (
                                            <div
                                                className="relative w-40 h-40 cursor-pointer overflow-hidden bg-gradient-to-br from-[#F5E3B3] to-[#E5D8C3] flex flex-col items-center justify-center p-4"
                                                onClick={() => navigate('zone-details', `zone=${encodeURIComponent(biz.zone_id)}`)}
                                            >
                                                <MapPin size={40} className="text-secondary-500 mb-2" />
                                                <span className="font-bold text-lg text-ink-strong text-center mb-1">
                                                    {biz.business_name || biz.zone_id}
                                                </span>
                                                <span className="text-sm text-ink-muted text-center">
                                                    {biz.business_description}
                                                </span>
                                            </div>
                                        ) : (
                                            <div
                                                className="relative w-40 h-40 cursor-pointer overflow-hidden"
                                                onClick={() => navigateToBusiness(biz.business_id)}
                                            >
                                                {/* Full cover image */}
                                                <img
                                                    src={biz.cover_image}
                                                    alt={biz.business_name}
                                                    className="w-full h-full object-cover"
                                                />
                                                {/* Name badge overlay */}
                                                <div className="absolute bottom-2 left-2 right-2 flex justify-center">
                                                    <span className="bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full font-bold text-xs text-ink-strong shadow-lg text-center">
                                                        {biz.business_name}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </Popup>
                                </Marker>
                            );
                        })}
                        {/* Modern map controls */}
                        <UnifiedMapControls userLocation={null} showLayerSwitcher={true} />
                    </MapContainer>
                </div>
            ) : (
                <Rolodex
                    items={filteredItems}
                    type={type}
                    chatId={chatId}
                    fromView={fromView}
                    zone={zone}
                    onDelete={onDelete}
                    favoritesLists={favoritesLists}
                    onToggleFavorite={onToggleFavorite}
                    onCreateFavoriteList={onCreateFavoriteList}
                />
            )}

            {filteredItems.length === 0 && (
                <p className="text-ink-muted font-medium text-center py-8">לא נמצאו תוצאות</p>
            )}
        </div>
    );
};